import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { WIKI_ARTICLE_KIND, articleTitle, isValidWikiArticle } from '@/lib/nip54';

function dedupeByAuthorAndSlug(events: NostrEvent[]): NostrEvent[] {
  const byKey = new Map<string, NostrEvent>();
  for (const event of events) {
    const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
    const key = `${event.pubkey}:${d}`;
    const existing = byKey.get(key);
    if (!existing || event.created_at > existing.created_at) {
      byKey.set(key, event);
    }
  }
  return [...byKey.values()];
}

/**
 * Searches wiki articles by title/content.
 *
 * Uses NIP-50 `search` filters where supported, plus a client-side
 * substring match over a broader recent-articles pull so results are
 * useful even on relays without search support.
 */
export function useWikiSearch(query: string) {
  const { nostr } = useNostr();
  const trimmed = query.trim();

  return useQuery({
    queryKey: ['wiki', 'search', trimmed],
    queryFn: async (c) => {
      if (!trimmed) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const [searched, recent] = await Promise.all([
        nostr.query([{ kinds: [WIKI_ARTICLE_KIND], search: trimmed, limit: 60 }], { signal }).catch(() => []),
        nostr.query([{ kinds: [WIKI_ARTICLE_KIND], limit: 300 }], { signal }).catch(() => []),
      ]);

      const needle = trimmed.toLowerCase();
      const matchedRecent = recent.filter((event) => {
        const title = articleTitle(event).toLowerCase();
        const summary = (event.tags.find(([n]) => n === 'summary')?.[1] ?? '').toLowerCase();
        return title.includes(needle) || summary.includes(needle) || event.content.toLowerCase().includes(needle);
      });

      const merged = dedupeByAuthorAndSlug([...searched, ...matchedRecent].filter(isValidWikiArticle));
      return merged.sort((a, b) => b.created_at - a.created_at);
    },
    enabled: trimmed.length > 0,
    staleTime: 30_000,
  });
}
